import { useState, useMemo } from "react";
import { Table, Container, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from 'react-select';
import { archiveMastersheetItem, updateCommentForMastersheetItem, updateStatusForMastersheetItem } from "./TenantForm/Helpers/api";
import FeatherIcon from 'feather-icons-react';
import './mastersheet.css';
import { useActiveColorConfig } from "./ColorConfig/Hooks/useActiveColorConfig";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge
} from 'reactstrap';
import Swal from "sweetalert2";

// Status options for the select field
const defaultStatusOptions = [
  { value: 'Start Pad', label: 'Start Pad' },
  { value: 'Change Rent', label: 'Change Rent' },
  { value: 'Do Not Renew', label: 'Do Not Renew' },
  { value: 'Not Renewing', label: 'Not Renewing' },
  { value: 'Lease Renewal Sent', label: 'Lease Renewal Sent' },
];

// Helper format functions
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) return 'N/A';
  return numericValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}-${month}-${year}`;
  } catch (e) {
    return 'Invalid Date';
  }
};

const formatPersonArray = (persons) => {
  if (!persons || !Array.isArray(persons) || persons.length === 0) return 'N/A';
  const names = persons.map(p => p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()).filter(name => name);
  if (names.length === 0) return 'N/A';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const formatTenants = (tenants) => {
  if (!tenants) return 'N/A';
  if (typeof tenants === 'string') {
    return tenants.trim() || 'N/A';
  }
  if (!Array.isArray(tenants) || tenants.length === 0) return 'N/A';
  return tenants
    .map((tenant) => {
      if (typeof tenant === 'object' && tenant) {
        if (tenant.name) return tenant.name;
        if (tenant.firstName && tenant.lastName) return `${tenant.firstName} ${tenant.lastName}`;
      }
      if (typeof tenant === 'string') return tenant;
      return 'Unknown Tenant';
    })
    .join(', ');
};

const formatPetsArray = (pets) => {
  if (!pets || !Array.isArray(pets) || pets.length === 0) return 'N/A';
  return pets.map(pet => `${pet.category} (${pet.breed}, ${pet.weightKg} kg)`).join("; ");
};

const formatSecurityDeposit = (deposit) => {
  if (!deposit) return 'N/A';
  if (typeof deposit === 'number') {
    return formatCurrency(deposit);
  }
  if (typeof deposit === 'string' && !isNaN(parseFloat(deposit))) {
    return formatCurrency(parseFloat(deposit));
  }
  if (typeof deposit === 'object') {
    let result = [];
    if (deposit.leaseDeposit) {
      result.push(`Lease: ${formatCurrency(deposit.leaseDeposit)} (${deposit.leaseRefundable ? 'Refundable' : 'Non-refundable'})`);
    }
    if (deposit.petDeposit) {
      result.push(`Pet: ${formatCurrency(deposit.petDeposit)} (${deposit.petRefundable ? 'Refundable' : 'Non-refundable'})`);
    }
    return result.length > 0 ? result.join('; ') : 'N/A';
  }
  return 'N/A';
};

const formatInsurance = (insurance) => {
  if (!insurance) return 'N/A';
  return `${insurance.company} (${insurance.policyNumber}), Expires: ${formatDate(insurance.insuranceExpiryDmy)}`;
};

const formatFeeItem = (item) => {
  if (!item) return 'N/A';
  return `${item.category}: ${formatCurrency(item.fee)}`;
};

const formatAccounts = (accounts) => {
  if (!accounts) return 'N/A';
  let result = [];
  if (accounts.heatUtilityAccount) result.push(`Heat: ${accounts.heatUtilityAccount}`);
  if (accounts.waterUtilityAccount) result.push(`Water: ${accounts.waterUtilityAccount}`);
  if (accounts.electricityUtilityAccount) result.push(`Elect: ${accounts.electricityUtilityAccount}`);
  return result.length > 0 ? result.join('; ') : 'N/A';
};

const formatParking = (parking) => {
  if (!parking || typeof parking !== 'object') return 'N/A';
  let result = [];
  if (parking.surfaceStallFee && parking.surfaceStallFee > 0) {
    result.push(`Surface: ${formatCurrency(parking.surfaceStallFee)}`);
  }
  if (parking.undergroundFee && parking.undergroundFee > 0) {
    result.push(`Underground: ${formatCurrency(parking.undergroundFee)}`);
  }
  return result.length > 0 ? result.join('; ') : 'No parking fees';
};

const formatLeaseDetails = (leaseDetails) => {
  if (!leaseDetails || typeof leaseDetails !== 'object') return 'N/A';
  let result = [];
  if (leaseDetails.leaseStartDMY) {
    result.push(`Start: ${formatDate(leaseDetails.leaseStartDMY)}`);
  }
  if (leaseDetails.leaseEndDMY) {
    result.push(`End: ${formatDate(leaseDetails.leaseEndDMY)}`);
  }
  return result.length > 0 ? result.join(' | ') : 'N/A';
};

const formatMailbox = (mailbox) => {
  if (!mailbox || typeof mailbox !== 'object') return 'N/A';
  let result = [];
  if (mailbox.module) result.push('Module');
  if (mailbox.compartment) result.push('Compartment');
  return result.length > 0 ? result.join(', ') : 'N/A';
};

const MasterSheetTable = ({ mastersheet, onEditTenant, propertyId, buildingName, onDataUpdate }) => {
  const { activeConfig, isLoading, error } = useActiveColorConfig(propertyId, buildingName);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { rowId, columnKey }
  const [editValue, setEditValue] = useState({ value: '', label: '' });
  const [statusOptions, setStatusOptions] = useState(defaultStatusOptions);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 }); // Track cursor position
  const { items = [] } = mastersheet || {};

  const { columns, targetColumns, totalTargets, feeColumns, totalRent } = useMemo(() => {
    if (!items || items.length === 0) return { columns: [], targetColumns: [], totalTargets: {}, feeColumns: [], totalRent: {} };

    const excludedKeys = ['_id', '__v', 'createdAt', 'updatedAt', 'mastersheet_id', 'primary_id'];
    const labelKeys = new Map();
    const targetCols = [];
    const feeCols = [];
    const targets = {};
    const rentTotals = {};
    let lastFeeIdx = -1;
    let lastTargetIdx = -1;
    let statusColumnIndex = -1;

    items.forEach(item => {
      Object.keys(item).forEach(key => {
        if (excludedKeys.includes(key)) return;
        if (key.endsWith('_label') && !key.includes('.')) {
          const baseKey = key.replace('_label', '');
          if (!labelKeys.has(baseKey)) {
            labelKeys.set(baseKey, {
              key: baseKey,
              label: item[key]
            });
          }
        }
      });
    });
    // Add meta.occupants column just before status column
    let columnsArray = Array.from(labelKeys.values());
    columnsArray.forEach((col, index) => {
      if (col.key.toLowerCase() === 'status') {
        statusColumnIndex = index;
      }
    });

    if (statusColumnIndex >= 0) {
      columnsArray.splice(statusColumnIndex, 0, {
        key: 'meta.occupants',
        label: 'Occupants'
      });
    } else {
      columnsArray.push({
        key: 'meta.occupants',
        label: 'Occupants'
      });
    }

    columnsArray.forEach((columnData, index) => {
      if (columnData.label.toLowerCase().includes('target')) {
        targetCols.push(columnData.key);
        lastTargetIdx = index;

        let targetTotal = 0;
        items.forEach(item => {
          const value = item[columnData.key];
          const numericValue = parseFloat(value);
          if (!isNaN(numericValue)) {
            targetTotal += numericValue;
          }
        });
        targets[columnData.key] = targetTotal;
      }
      if (columnData.key.toLowerCase().includes('fee') || columnData.key === 'basicRent') {
        feeCols.push(columnData.key);
        lastFeeIdx = index;

        let rentTotal = 0;
        items.forEach(item => {
          let value = item[columnData.key];
          if (typeof value === 'object' && value !== null) {
            if (columnData.key === 'parking') {
              if (value.surfaceStallFee) value = value.surfaceStallFee;
              if (value.undergroundFee) value = value.undergroundFee || value.surfaceStallFee;
            } else if (columnData.key === 'storage') {
              value = value.fee;
            }
          }
          const numericValue = parseFloat(value);
          if (!isNaN(numericValue)) {
            rentTotal += numericValue;
          }
        });
        rentTotals[columnData.key] = rentTotal;
      }
    });

    if (lastTargetIdx >= 0 || lastFeeIdx >= 0) {
      const totalTargetColumn = {
        key: '__target_total__',
        label: 'Target Total',
        isVirtual: true,
        isTargetTotal: true
      };
      const totalRentColumn = {
        key: '__total_rent__',
        label: 'Total Rent',
        isVirtual: true,
        isRentTotal: true
      };

      if (lastTargetIdx >= 0 && lastFeeIdx >= 0) {
        if (targetCols.length > 1) {
          columnsArray.splice(lastTargetIdx + 1, 0, totalTargetColumn);
          if (lastFeeIdx > lastTargetIdx) {
            columnsArray.splice(lastFeeIdx + 2, 0, totalRentColumn);
          } else {
            columnsArray.splice(lastFeeIdx + 1, 0, totalRentColumn);
          }
        } else {
          columnsArray.splice(lastFeeIdx + 1, 0, totalRentColumn);
        }
      } else if (lastTargetIdx >= 0) {
        if (targetCols.length > 1) {
          columnsArray.splice(lastTargetIdx + 1, 0, totalTargetColumn);
          columnsArray.push(totalRentColumn);
        } else {
          columnsArray.push(totalRentColumn);
        }
      } else if (lastFeeIdx >= 0) {
        columnsArray.splice(lastFeeIdx + 1, 0, totalRentColumn);
        if (targetCols.length > 1) {
          columnsArray.push(totalTargetColumn);
        }
      }
    }

    return {
      columns: columnsArray,
      targetColumns: targetCols,
      totalTargets: targets,
      feeColumns: feeCols,
      totalRent: rentTotals
    };
  }, [items]);

  const calculateRowTargetTotal = (row) => {
    let rowTotal = 0;
    targetColumns.forEach(targetKey => {
      const value = row[targetKey];
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        rowTotal += numericValue;
      }
    });
    return rowTotal;
  };

  const calculateRowRentTotal = (row) => {
    let rowTotal = 0;
    feeColumns.forEach(feeKey => {
      let value = row[feeKey];
      if (typeof value === 'object' && value !== null) {
        if (feeKey === 'parking') {
          if (value.surfaceStallFee) value = value.surfaceStallFee;
          if (value.undergroundFee) value = value.undergroundFee || value.surfaceStallFee;
        } else if (feeKey === 'storage') {
          value = value.fee;
        }
      }
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        rowTotal += numericValue;
      }
    });
    return rowTotal;
  };

  const calculateGrandTargetTotal = () => {
    return Object.values(totalTargets).reduce((sum, val) => sum + val, 0);
  };

  const calculateGrandRentTotal = () => {
    return Object.values(totalRent).reduce((sum, val) => sum + val, 0);
  };

  const isCurrencyColumn = (key) => {
    const currencyKeywords = ['rent', ' deposit', 'fee', 'price', 'cost', 'amount', 'payment', 'parking', 'storage', 'targetTelus', '__target_total__', '__total_rent__'];
    return currencyKeywords.some(keyword => key.toLowerCase().includes(keyword));
  };

  const isNumericColumn = (key) => {
    const numericKeywords = ['area', 'sqft', 'weight', 'age', 'months', 'years', 'size', 'capacity'];
    return numericKeywords.some(keyword => key.toLowerCase().includes(keyword));
  };

  const getCellStyles = (row, columnKey) => {
    let cellStyles = {};
    let highestPriority = Infinity;

    if (activeConfig && activeConfig.colorRules && !isLoading && !error) {
      activeConfig.colorRules
        .filter((rule) => rule.isActive && rule.appliesTo === "cell")
        .forEach((rule) => {
          if (rule.ruleType === 'static' && rule.field === columnKey && rule.priority < highestPriority) {
            cellStyles = {
              backgroundColor: rule.staticColor.backgroundColor,
              color: rule.staticColor.textColor,
              borderColor: rule.staticColor.borderColor,
            };
            highestPriority = rule.priority;
          }

          if (rule.ruleType === 'conditional' && rule.conditions && rule.conditions.length > 0) {
            rule.conditions.forEach((condition) => {
              let applies = false;

              const ruleFieldValue = row[rule.field];
              let conditionValue = condition.value;

              if (typeof conditionValue === 'string' && conditionValue.startsWith('FIELD-')) {
                const referenceField = conditionValue.replace('FIELD-', '');
                conditionValue = row[referenceField];

                const ruleValueNum = parseFloat(ruleFieldValue);
                const conditionValueNum = parseFloat(conditionValue);

                if (isNaN(ruleValueNum) || isNaN(conditionValueNum)) {
                  return;
                }

                switch (condition.operator.toLowerCase()) {
                  case 'equals':
                    applies = ruleValueNum === conditionValueNum;
                    break;
                  case 'not_equals':
                    applies = ruleValueNum !== conditionValueNum;
                    break;
                  case 'greater_than':
                    applies = ruleValueNum > conditionValueNum;
                    break;
                  case 'less_than':
                    applies = ruleValueNum < conditionValueNum;
                    break;
                  case 'greater_equal':
                    applies = ruleValueNum >= conditionValueNum;
                    break;
                  case 'less_equal':
                    applies = ruleValueNum <= conditionValueNum;
                    break;
                  default:
                    break;
                }
              } else {
                switch (condition.operator.toLowerCase()) {
                  case 'equals':
                    applies = ruleFieldValue === conditionValue;
                    break;
                  case 'not_equals':
                    applies = ruleFieldValue !== conditionValue;
                    break;
                  case 'greater_than':
                    applies = !isNaN(parseFloat(ruleFieldValue)) && !isNaN(parseFloat(conditionValue)) && parseFloat(ruleFieldValue) > parseFloat(conditionValue);
                    break;
                  case 'less_than':
                    applies = !isNaN(parseFloat(ruleFieldValue)) && !isNaN(parseFloat(conditionValue)) && parseFloat(ruleFieldValue) < parseFloat(conditionValue);
                    break;
                  case 'greater_equal':
                    applies = !isNaN(parseFloat(ruleFieldValue)) && !isNaN(parseFloat(conditionValue)) && parseFloat(ruleFieldValue) >= parseFloat(conditionValue);
                    break;
                  case 'less_equal':
                    applies = !isNaN(parseFloat(ruleFieldValue)) && !isNaN(parseFloat(conditionValue)) && parseFloat(ruleFieldValue) <= parseFloat(conditionValue);
                    break;
                  case 'contains':
                    applies = typeof ruleFieldValue === 'string' && typeof conditionValue === 'string' && ruleFieldValue.includes(conditionValue);
                    break;
                  case 'not_contains':
                    applies = typeof ruleFieldValue === 'string' && typeof conditionValue === 'string' && !ruleFieldValue.includes(conditionValue);
                    break;
                  case 'in':
                    applies = Array.isArray(conditionValue) && conditionValue.includes(ruleFieldValue);
                    break;
                  case 'not_in':
                    applies = Array.isArray(conditionValue) && !conditionValue.includes(ruleFieldValue);
                    break;
                  case 'is_empty':
                    applies = ruleFieldValue === null || ruleFieldValue === undefined || ruleFieldValue === '';
                    break;
                  case 'is_not_empty':
                    applies = ruleFieldValue !== null && ruleFieldValue !== undefined && ruleFieldValue !== '';
                    break;
                  case 'starts_with':
                    applies = typeof ruleFieldValue === 'string' && typeof conditionValue === 'string' && ruleFieldValue.startsWith(conditionValue);
                    break;
                  case 'ends_with':
                    applies = typeof ruleFieldValue === 'string' && typeof conditionValue === 'string' && ruleFieldValue.endsWith(conditionValue);
                    break;
                  default:
                    break;
                }
              }

              if (applies && rule.priority < highestPriority) {
                const shouldApplyToThisColumn =
                  rule.field === columnKey || // Rule is for this field directly
                  (condition.affectedFields && condition.affectedFields.includes(columnKey)); // Or this field is in affectedFields

                if (shouldApplyToThisColumn) {
                  cellStyles = {
                    backgroundColor: condition.color.backgroundColor,
                    color: condition.color.textColor,
                    borderColor: condition.color.borderColor,
                  };
                  highestPriority = rule.priority;
                }
              }
            });
          }
        });
    }

    if ((columnKey === 'leaseEndDmy' || columnKey === 'insuranceExpiry') && highestPriority === Infinity) {
      const leaseEndDateStr = row[columnKey];
      if (leaseEndDateStr && leaseEndDateStr !== 'N/A') {
        try {
          const leaseEndDate = new Date(leaseEndDateStr);
          const currentDate = new Date();
          const timeDiff = leaseEndDate - currentDate;
          const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

          if (daysDiff <= 30) {
            cellStyles = {
              backgroundColor: '#FF4545',
              color: '#FFFFFF',
            };
          } else if (daysDiff <= 60) {
            cellStyles = {
              backgroundColor: '#FCEF91',
              color: '#000000',
            };
          }
        } catch (e) {
          console.error('Error parsing lease end date:', e);
        }
      }
    }

    return cellStyles;
  };

  const columnStats = useMemo(() => {
    if (!items || items.length === 0 || !columns) return {};

    const stats = {};

    columns.forEach(column => {
      const { key } = column;

      if (key === '__target_total__') {
        const grandTotal = calculateGrandTargetTotal();
        stats[key] = {
          type: 'currency',
          value: grandTotal,
          label: formatCurrency(grandTotal),
          isGrandTotal: true
        };
        return;
      }

      if (key === '__total_rent__') {
        const grandRentTotal = calculateGrandRentTotal();
        stats[key] = {
          type: 'currency',
          value: grandRentTotal,
          label: formatCurrency(grandRentTotal),
          isGrandTotal: true
        };
        return;
      }

      if (key === 'meta.occupants') {
        let totalOccupants = 0;
        items.forEach(item => {
          const occupants = item.meta?.occupants;

          // Count meta.occupants (excluding N/A names)
          if (Array.isArray(occupants)) {
            totalOccupants += occupants.filter(occupant =>
              occupant &&
              occupant.name &&
              occupant.name.trim() !== '' &&
              occupant.name.toLowerCase() !== 'n/a'
            ).length;
          }

          // Count tenants from other fields
          const tenants = item.tenants || item.tenant;
          if (Array.isArray(tenants)) {
            totalOccupants += tenants.length;
          } else if (typeof tenants === 'string' && tenants.trim()) {
            const separators = /,|\band\b|&/;
            const tenantsList = tenants.split(separators)
              .map(name => name.trim())
              .filter(name => name.length > 0 && name.toLowerCase() !== 'n/a');
            totalOccupants += tenantsList.length;
          }

          // Count occupants field if it exists
          const occupantsField = item.occupants;
          if (Array.isArray(occupantsField)) {
            totalOccupants += occupantsField.filter(occupant =>
              occupant &&
              (occupant.name ? occupant.name.toLowerCase() !== 'n/a' : true)
            ).length;
          }
        });

        stats[key] = {
          type: 'count',
          value: totalOccupants,
          label: `Total: ${totalOccupants}`
        };
        return;
      }

      if (key.toLowerCase().includes('area') || key.toLowerCase().includes('sqft')) {
        stats[key] = {
          type: 'none',
          value: 0,
          label: 'N/A'
        };
        return;
      }

      let total = 0;
      let count = 0;
      let arrayTotal = 0;

      items.forEach(item => {
        const value = item[key];

        if (key === 'securityDeposit' && value && typeof value === 'object') {
          let depositTotal = 0;
          if (value.leaseDeposit && !isNaN(parseFloat(value.leaseDeposit))) {
            depositTotal += parseFloat(value.leaseDeposit);
          }
          if (value.petDeposit && !isNaN(parseFloat(value.petDeposit))) {
            depositTotal += parseFloat(value.petDeposit);
          }
          if (depositTotal > 0) {
            total += depositTotal;
            count++;
          }
          return;
        }

        if ((key === 'parking' || key === 'storage') && value && typeof value === 'object') {
          if (value.fee && !isNaN(parseFloat(value.fee))) {
            const feeValue = parseFloat(value.fee);
            total += feeValue;
            count++;
          }
          return;
        }

        if (Array.isArray(value)) {
          arrayTotal += value.length;
        } else if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            total += numValue;
            count++;
          }
        } else if (typeof value === 'boolean') {
          if (value === true) {
            total++;
          }
          count++;
        } else if (typeof value === 'object' && value !== null) {
          if (key !== 'securityDeposit' && key !== 'parking' && key !== 'storage') {
            total++;
          }
        } else if (value && value !== 'N/A') {
          count++;
        }
      });

      if (targetColumns.includes(key) && totalTargets[key] !== undefined) {
        stats[key] = {
          type: 'currency',
          value: totalTargets[key],
          label: formatCurrency(totalTargets[key])
        };
      } else if (feeColumns.includes(key) && totalRent[key] !== undefined) {
        stats[key] = {
          type: 'currency',
          value: totalRent[key],
          label: formatCurrency(totalRent[key])
        };
      } else if (arrayTotal > 0) {
        stats[key] = {
          type: 'array',
          value: arrayTotal,
          label: `Total: ${arrayTotal}`
        };
      } else if (total > 0 && isCurrencyColumn(key)) {
        stats[key] = {
          type: 'currency',
          value: total,
          label: formatCurrency(total)
        };
      } else if (total > 0 && isNumericColumn(key)) {
        stats[key] = {
          type: 'numeric',
          value: total,
          average: count > 0 ? (total / count).toFixed(2) : 0,
          label: `Total: ${total.toFixed(2)} | Avg: ${count > 0 ? (total / count).toFixed(2) : 0}`
        };
      } else if (count > 0) {
        stats[key] = {
          type: 'count',
          value: count,
          label: `Count: ${count}`
        };
      } else {
        stats[key] = {
          type: 'none',
          value: 0,
          label: 'N/A'
        };
      }
    });

    return stats;
  }, [items]);

  const getCellValue = (item, path) => {
    if (path === '__target_total__') {
      const rowTotal = calculateRowTargetTotal(item);
      return rowTotal;
    }

    if (path === '__total_rent__') {
      const rowRentTotal = calculateRowRentTotal(item);
      return rowRentTotal;
    }

    if (path === 'meta.occupants') {
      const occupants = item.meta?.occupants;
      let totalCount = 0;

      // Count meta.occupants (excluding N/A names)
      if (Array.isArray(occupants)) {
        totalCount += occupants.filter(occupant =>
          occupant &&
          occupant.name &&
          occupant.name.trim() !== '' &&
          occupant.name.toLowerCase() !== 'n/a'
        ).length;
      }

      const tenants = item.tenants || item.tenant;
      if (Array.isArray(tenants)) {
        totalCount += tenants.length;
      } else if (typeof tenants === 'string' && tenants.trim()) {
        const separators = /,|\band\b|&/;
        const tenantsList = tenants.split(separators)
          .map(name => name.trim())
          .filter(name => name.length > 0 && name.toLowerCase() !== 'n/a');
        totalCount += tenantsList.length;
      }

      const occupantsField = item.occupants;
      if (Array.isArray(occupantsField)) {
        totalCount += occupantsField.filter(occupant =>
          occupant &&
          (occupant.name ? occupant.name.toLowerCase() !== 'n/a' : true)
        ).length;
      }

      return totalCount.toString();
    }

    if (path === 'tenants' || path === 'tenant') {
      return formatTenants(item[path]);
    }
    if (path === 'occupants') {
      return formatPersonArray(item.occupants);
    }
    if (path === 'targetTelus') {
      return formatCurrency(item.targetTelus);
    }
    if (path === 'pets') {
      return formatPetsArray(item.pets);
    }
    if (path === 'securityDeposit' || path === 'sd') {
      return formatSecurityDeposit(item.securityDeposit || item.sd);
    }
    if (path === 'insurance') {
      return formatInsurance(item.insurance);
    }
    if (path === 'parking') {
      return formatParking(item.parking);
    }
    if (path === 'storage') {
      return formatFeeItem(item.storage);
    }
    if (path === 'accounts') {
      return formatAccounts(item.accounts);
    }
    if (path === 'leaseDetails') {
      return formatLeaseDetails(item.leaseDetails);
    }
    if (path === 'mailbox') {
      return formatMailbox(item.mailbox);
    }

    if (path === 'comments') {
      const value = item[path];
      if (value && typeof value === 'object' && value.richText) {
        // Handle richText object by extracting text from each entry
        return value.richText
          .map(entry => entry.text || '')
          .join('')
          .trim() || 'N/A';
      }
      return value || 'N/A';
    }

    const value = item[path];
    if (value === null || value === undefined || value === '') return 'N/A';

    if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value))) {
      return formatDate(value);
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    return value;
  };

  const isCommentsColumn = (column) => {
    return column && (column.label.toLowerCase() === 'comments' || column.key.toLowerCase() === 'comments');
  };

  const isStatusColumn = (column) => {
    return column && (column.label.toLowerCase() === 'status' || column.key.toLowerCase() === 'status');
  };

  const isTargetTotalColumn = (column) => {
    return column && (column.key === '__target_total__' || column.isTargetTotal);
  };

  const isRentTotalColumn = (column) => {
    return column && (column.key === '__total_rent__' || column.isRentTotal);
  };

  const handleCellClick = (row, column, currentValue, event) => {
    if (!isCommentsColumn(column) && !isStatusColumn(column)) return;
    event.stopPropagation();
    setEditingCell({ rowId: row._id, columnKey: column.key });
    if (isCommentsColumn(column)) {
      setEditValue(currentValue === 'N/A' ? '' : currentValue);
    } else if (isStatusColumn(column)) {
      const currentOption = statusOptions.find(opt => opt.value === currentValue) ||
        (currentValue && currentValue !== 'N/A' ? { value: currentValue, label: currentValue } : null);
      setEditValue(currentOption);
    }
  };

  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleStatusChange = (selectedOption, row) => {
    if (selectedOption) {
      handleEditSubmit(row, selectedOption);
    }
    setEditValue(selectedOption || { value: '', label: '' });
  };

  const handleStatusInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setEditValue({ value: inputValue, label: inputValue });
    }
  };

  const handleStatusKeyDown = (event, row) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      const inputValue = event.target.value;

      if (inputValue && inputValue.trim()) {
        const existingOption = statusOptions.find(opt =>
          opt.value.toLowerCase() === inputValue.toLowerCase()
        );

        if (existingOption) {
          handleEditSubmit(row, existingOption);
        } else {
          const newOption = { value: inputValue.trim(), label: inputValue.trim() };
          setStatusOptions(prev => [...prev, newOption]);
          handleEditSubmit(row, newOption);
        }
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleEditCancel();
    }
  };

  const handleEditSubmit = async (row, selectedOption = null) => {
    if (!editingCell) return;

    const column = columns.find(col => col.key === editingCell.columnKey);
    if (!column) return;

    const originalValue = getCellValue(row, editingCell.columnKey);
    let newValue = isStatusColumn(column) ? (selectedOption ? selectedOption.value : editValue?.value || 'N/A') : editValue;

    if (isStatusColumn(column) && newValue === originalValue) {
      setEditingCell(null);
      setEditValue({ value: '', label: '' });
      return;
    }

    if (!isStatusColumn(column) && (newValue === originalValue || (newValue === '' && originalValue === 'N/A'))) {
      setEditingCell(null);
      setEditValue({ value: '', label: '' });
      return;
    }

    const title = isStatusColumn(column) ? 'Update Status' : 'Update Comment';
    const text = isStatusColumn(column) ? `Do you want to update the status to "${newValue}" for Unit ${row.unitNo}?` : `Do you want to save this comment for Unit ${row.unitNo}?`;

    const result = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, save it!',
      cancelButtonText: 'Cancel',
      customClass: {
        container: isStatusColumn(column) ? 'status-update-dialog' : 'comment-update-dialog'
      }
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: `Updating ${isStatusColumn(column) ? 'Status' : 'Comment'}`,
        text: 'Please wait...',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        let updatedItem;
        if (isStatusColumn(column)) {
          updatedItem = await updateStatusForMastersheetItem(row._id, newValue);
        } else {
          updatedItem = await updateCommentForMastersheetItem(row._id, newValue);
        }

        if (updatedItem) {
          await Swal.fire({
            title: 'Success!',
            text: `${isStatusColumn(column) ? 'Status' : 'Comment'} has been updated successfully.`,
            icon: 'success',
            confirmButtonText: 'OK'
          });
          if (onDataUpdate) {
            onDataUpdate();
          }
        } else {
          await Swal.fire({
            title: 'Error!',
            text: `Failed to update ${isStatusColumn(column) ? 'status' : 'comment'}. Please try again.`,
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      } catch (error) {
        console.error(`Error updating ${isStatusColumn(column) ? 'status' : 'comment'}:`, error);
        await Swal.fire({
          title: 'Error!',
          text: `An error occurred while updating the ${isStatusColumn(column) ? 'status' : 'comment'}.`,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    }

    setEditingCell(null);
    setEditValue({ value: '', label: '' });
  };

  const handleEditKeyPress = (e, row) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSubmit(row);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue({ value: '', label: '' });
  };

  const handleRowClick = (row, event) => {
    event.stopPropagation();
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleMouseMove = (event) => {
    setCursorPosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const UnitDetailsModal = ({ open, onClose, row, propertyId, buildingName, onDataUpdate }) => {
    if (!row) return null;

    const hasActiveLease = row.lease_status?.has_active_lease === true;
    const tenantInfo = formatTenants(row.tenants || row.tenant);
    const unitNo = row.unitNo || 'N/A';
    const hasTenant = tenantInfo !== 'N/A';

    const handleAction = (action, e) => {
      e.stopPropagation();
      onClose();
      if (action === 'edit') {
        // Trigger edit tenant action
        if (onEditTenant) {
          onEditTenant({
            ...row,
            propertyId,
            buildingName,
          });
        }
      } else if (action === 'manageLease') {
        // Always navigate to the new lease page
        const leasePath = `/leases/new_lease/${unitNo}/${propertyId}/${buildingName || ''}`;
        window.open(leasePath, '_blank');
      }
    };

    const handleArchive = async (e) => {
      e.stopPropagation();

      const { value: archivedReason, isConfirmed } = await Swal.fire({
        title: 'Are you sure you want to archive this item?',
        text: 'This action cannot be undone. Archived tenant records can be viewed in the Archive Mastersheet for future reference.',
        input: 'text',
        inputLabel: 'Reason for archiving',
        inputPlaceholder: 'Enter reason here...',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, archive it!',
        cancelButtonText: 'Cancel',
        inputValidator: (value) => {
          if (!value) return 'Reason is required';
        },
        customClass: {
          container: 'archive-dialog',
        },
      });

      if (!isConfirmed) return;

      Swal.fire({
        title: 'Archiving Unit',
        text: `Archiving Unit ${unitNo}...`,
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const { success, data } = await archiveMastersheetItem(
          row.mastersheet_id,
          propertyId,
          buildingName,
          unitNo,
          archivedReason
        );

        if (!success) {
          throw new Error(data?.message || 'Failed to archive unit');
        }

        await Swal.fire({
          title: 'Success!',
          text: data?.message || 'Unit has been archived successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        });

        onClose();
        if (onDataUpdate) {
          onDataUpdate();
        }
      } catch (error) {
        console.error('Error archiving unit:', error);
        await Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to archive unit. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    };

    return (
      <Modal isOpen={open} toggle={onClose} centered size="md">
        <ModalHeader toggle={onClose} className="bg-light">
          <div className="w-100 text-center">🏠 Unit {unitNo} Details</div>
        </ModalHeader>

        <ModalBody className="px-4">
          {hasTenant ? (
            <div className="mb-3">
              <div className="mb-2 d-flex justify-content-between align-items-center">
                <span className="text-primary">👤 Tenant: {tenantInfo}</span>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="archive-tooltip" style={{ zIndex: 2000 }}>
                      Archive: This action is irreversible, but archived tenant records can be viewed in the Archive Mastersheet for future reference.
                    </Tooltip>
                  }
                >
                  <FeatherIcon
                    icon="hard-drive"
                    size={16}
                    className="moveout-icon text-danger cursor-pointer"
                    title="Archive"
                    aria-label="Archive"
                    onClick={handleArchive}
                  />
                </OverlayTrigger>
              </div>
              <div className="mb-2">
                <span className="text-primary">💲 Base Rent: {formatCurrency(row.basicRent)}</span>
              </div>
              <div className="mb-2">
                <span className="text-primary">🏢 Unit No.: {unitNo}</span>
              </div>
              <div>
                <span className="text-primary">📄 Lease Status: {hasActiveLease ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          ) : (
            <p className="text-muted text-center mb-3">
              No tenants assigned to Unit <strong>{unitNo}</strong>.<br />
              Select an action below.
            </p>
          )}
        </ModalBody>

        <ModalFooter className="justify-content-between px-4">
          <Button color="warning" onClick={(e) => handleAction('edit', e)}>
            ✏️ Edit Tenant
          </Button>
          <div className="ms-auto d-flex gap-2">
            <Button
              color={hasActiveLease ? 'success' : 'primary'}
              onClick={(e) => handleAction('manageLease', e)}
            >
              {hasActiveLease ? '📋 Manage Lease' : '➕ Manage Lease'}
            </Button>
            <Button color="secondary" outline onClick={onClose}>
              Close
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  const hasTenants = (row) => {
    const tenants = row.tenants;
    const tenant = row.tenant;
    return (
      (typeof tenants === 'string' && tenants.trim() !== '') ||
      (Array.isArray(tenants) && tenants.length > 0) ||
      (typeof tenant === 'string' && tenant.trim() !== '') ||
      (Array.isArray(tenant) && tenant.length > 0)
    );
  };

  return (
    <Container fluid className="position-relative">
      <UnitDetailsModal
        open={modalOpen}
        propertyId={propertyId}
        buildingName={buildingName}
        onClose={handleModalClose}
        row={selectedRow}
        onDataUpdate={onDataUpdate}
      />
      {hoveredRow !== null && items[hoveredRow]?.unitNo && (
        <div
          style={{
            position: 'fixed',
            top: cursorPosition.y + 0,
            left: cursorPosition.x + 10,
            backgroundColor: '#333',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '4px',
            zIndex: 1000,
            fontSize: '12px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Unit {items[hoveredRow].unitNo}
        </div>
      )}
      <div className="table-container">
        <Table
          bordered
          hover
          responsive
          className="mastersheet-table"
          onMouseMove={handleMouseMove}
        >
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={column.key === 'unitNo' ? 'sticky-unit-no' : ''}
                  style={isTargetTotalColumn(column) || isRentTotalColumn(column) ? {
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    background: '#03346E'
                  } : isStatusColumn(column) ? {
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    minWidth: '200px'
                  } : {}}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`table-row ${hoveredRow === rowIndex ? "hovered-row" : ""} ${hasTenants(row) ? "" : "table-danger"}`}
                  onMouseEnter={() => setHoveredRow(rowIndex)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={(e) => handleRowClick(row, e)}
                  style={{ cursor: 'pointer' }}
                >
                  {columns.map((column, colIndex) => {
                    const value = getCellValue(row, column.key);
                    let displayValue = value;

                    if (isCurrencyColumn(column.key) && !isNaN(parseFloat(value)) && value !== 'N/A') {
                      displayValue = formatCurrency(value);
                    }

                    let cellClass = column.key === 'unitNo' ? 'sticky-unit-no' : '';
                    if (column.key === 'unitNo') {
                      cellClass += value && value !== 'N/A' ? ' table-success' : ' table-danger';
                    }

                    const cellStyle = {};

                    if (typeof displayValue === 'string' && displayValue.length > 50) {
                      cellStyle.maxWidth = '200px';
                      cellStyle.overflow = 'hidden';
                      cellStyle.textOverflow = 'ellipsis';
                      cellStyle.whiteSpace = 'nowrap';
                    }

                    const isEditing = editingCell &&
                      editingCell.rowId === row._id &&
                      editingCell.columnKey === column.key;

                    if (isTargetTotalColumn(column) || isRentTotalColumn(column)) {
                      cellStyle.backgroundColor = value && value !== 'N/A' ? "#DDF6D2" :
                        (hoveredRow === rowIndex ? 'linear-gradient(135deg, #e6f0fa 0%, #d0e7ff 100%)' : 'transparent');
                    }

                    if (isCommentsColumn(column) || isStatusColumn(column)) {
                      cellStyle.cursor = 'pointer';
                      cellStyle.backgroundColor = isEditing ? '#f8f9fa' :
                        (hoveredRow === rowIndex ? '#e3f2fd' : 'transparent');
                    }

                    const colorConfigStyles = getCellStyles(row, column.key);
                    if (colorConfigStyles.backgroundColor || colorConfigStyles.color || colorConfigStyles.borderColor) {
                      if (!(isEditing && (isCommentsColumn(column) || isStatusColumn(column)))) {
                        Object.assign(cellStyle, colorConfigStyles);
                      }
                    }

                    return (
                      <td
                        key={colIndex}
                        className={`${cellClass} ${hoveredRow === rowIndex ? "hovered-row" : ""} ${isCommentsColumn(column) ? 'comments-cell' : ''} ${isStatusColumn(column) ? 'status-cell' : ''}`}
                        style={cellStyle}
                        title={typeof displayValue === 'string' && displayValue.length > 50 ? displayValue : ""}
                        onClick={(e) => handleCellClick(row, column, displayValue, e)}
                      >
                        {isEditing ? (
                          isStatusColumn(column) ? (
                            <Select
                              options={statusOptions}
                              value={editValue}
                              onChange={(option) => handleStatusChange(option, row)}
                              onInputChange={handleStatusInputChange}
                              onKeyDown={(e) => handleStatusKeyDown(e, row)}
                              isClearable
                              placeholder="Select or type status..."
                              autoFocus
                              menuPortalTarget={document.body}
                              styles={{
                                container: (base) => ({
                                  ...base,
                                  width: '100%',
                                }),
                                control: (base) => ({
                                  ...base,
                                  border: 'none',
                                  boxShadow: 'none',
                                  background: 'transparent',
                                  minHeight: 'auto',
                                  padding: '4px',
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          ) : (
                            <input
                              type="text"
                              value={editValue}
                              onChange={handleEditChange}
                              onBlur={() => handleEditSubmit(row)}
                              onKeyDown={(e) => handleEditKeyPress(e, row)}
                              autoFocus
                              style={{
                                width: '100%',
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                padding: '4px'
                              }}
                            />
                          )
                        ) : (
                          <span>
                            {displayValue}
                            {(isCommentsColumn(column) || isStatusColumn(column)) && (
                              <small className="text-muted ms-1"><FeatherIcon icon="edit" size="16" style={{ marginLeft: 4, verticalAlign: 'middle' }} /></small>
                            )}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#004085')}
              >
                {columns.map((column, index) => {
                  const stat = columnStats[column.key] || { label: '-' };

                  const statStyles = {
                    array: {
                      padding: '12px',
                      textAlign: 'center',
                    },
                    count: {
                      padding: '12px',
                      textAlign: 'center',
                    },
                    numeric: {
                      padding: '12px',
                      textAlign: 'center',
                    },
                    currency: {
                      padding: '12px',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    },
                    none: {
                      padding: '12px',
                      textAlign: 'center',
                    },
                  };

                  const statStyle = statStyles[stat.type] || statStyles.none;

                  return (
                    <td
                      style={{
                        ...statStyle,
                        backgroundColor: '#ECFAE5',
                        fontWeight: '500',
                        borderTop: '1px solid #003366',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {stat.label}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
    </Container>
  );
};

export { MasterSheetTable };